import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiClient } from '../client'
import type { PrdDraft } from '../types'

interface EditPrdDraftPayload {
  title?: string
  body?: string
}

export function useUpdatePrdDraft(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: EditPrdDraftPayload) =>
      apiClient<PrdDraft>(
        `/api/v1/agents/${agentId}/prd-drafts/${draftId}`,
        { method: 'PATCH', body: JSON.stringify(payload) },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['prd-drafts', 'detail', agentId, draftId], data)
      queryClient.invalidateQueries({ queryKey: ['prd-drafts', 'list', agentId] })
      toast.success('PRD updated')
    },
    onError: () => {
      toast.error('Failed to update PRD. Please try again.')
    },
  })
}

export function usePublishPrdDraft(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient<PrdDraft>(
        `/api/v1/agents/${agentId}/prd-drafts/${draftId}/publish`,
        { method: 'POST' },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['prd-drafts', 'detail', agentId, draftId], data)
      queryClient.invalidateQueries({ queryKey: ['prd-drafts', 'list', agentId] })
      toast.success('PRD published')
    },
    onError: () => {
      toast.error('Failed to publish PRD. Please try again.')
    },
  })
}

export function useUnpublishPrdDraft(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient<PrdDraft>(
        `/api/v1/agents/${agentId}/prd-drafts/${draftId}/unpublish`,
        { method: 'POST' },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['prd-drafts', 'detail', agentId, draftId], data)
      queryClient.invalidateQueries({ queryKey: ['prd-drafts', 'list', agentId] })
      toast.success('PRD moved back to drafts')
    },
    onError: () => {
      toast.error('Failed to unpublish PRD. Please try again.')
    },
  })
}

export function useRejectPrdDraft(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient<PrdDraft>(
        `/api/v1/agents/${agentId}/prd-drafts/${draftId}/reject`,
        { method: 'POST' },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['prd-drafts', 'detail', agentId, draftId], data)
      queryClient.invalidateQueries({ queryKey: ['prd-drafts', 'list', agentId] })
      toast.success('PRD rejected')
    },
    onError: () => {
      toast.error('Failed to reject PRD. Please try again.')
    },
  })
}

interface ExportToDrivePayload {
  connectorId: string
  folderId?: string
}

export function useExportPrdToDrive(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ExportToDrivePayload) =>
      apiClient<PrdDraft>(
        `/api/v1/agents/${agentId}/prd-drafts/${draftId}/export/drive`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          // 401 = Drive folder access denied (revoked OAuth scope, "My Drive"
          // root, etc.). Surface inline instead of logging the user out.
          skipAuthRedirectOn401: true,
        },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['prd-drafts', 'detail', agentId, draftId], data)
      queryClient.invalidateQueries({ queryKey: ['prd-drafts', 'list', agentId] })
      toast.success('Exported to Google Drive')
    },
    onError: () => {
      toast.error('Failed to export to Google Drive. Please try again.')
    },
  })
}

export type ClickUpDocParentType = 'space' | 'folder' | 'list'

interface ExportToClickUpPayload {
  connectorId: string
  workspaceId: string
  parentId: string
  parentType: ClickUpDocParentType
}

export function useExportPrdToClickUp(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ExportToClickUpPayload) =>
      apiClient<PrdDraft>(
        `/api/v1/agents/${agentId}/prd-drafts/${draftId}/export/clickup`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          // 401 = ClickUp OAuth/permission failure, not Auth0 expiry.
          skipAuthRedirectOn401: true,
        },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['prd-drafts', 'detail', agentId, draftId], data)
      queryClient.invalidateQueries({ queryKey: ['prd-drafts', 'list', agentId] })
      toast.success('Exported to ClickUp as a Doc')
    },
    onError: () => {
      toast.error('Failed to export to ClickUp. Please try again.')
    },
  })
}
